import { Group, Stack } from '@mantine/core';
import { useNavigate } from 'react-router';
import type { Initiative } from '../../../../api/initiative-client/initiative-client.type';
import { InitiativeStatus } from '../../../../api/initiative-client/initiative-client.type';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../helpers/date-time-helpers';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { resetRelatedTasksTabState } from '../../../../store/strategy-store/strategy-store.actions';
import { primaryBase, secondaryBase, skyLight } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { Label } from '../filters/status-label';
import { getSourceIcon, getStatusColor } from './main-section.helpers';

export const InitiativeIDCell = ({ initiative }: { initiative: Initiative }) => {
  const navigate = useNavigate();
  const portfolio = useGlobalStore((state) => state.portfolio);

  if (!initiative.key || !portfolio) {
    return null;
  }

  return (
    <Label color={getStatusColor(initiative.status?.by_tasks?.status || InitiativeStatus.Unknown)}>
      <Text
        size="small"
        lineHeight="none"
        color={secondaryBase}
        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
        onClick={(e) => {
          e.stopPropagation();
          resetRelatedTasksTabState();
          navigate(`/application/strategy/${portfolio.id}/${initiative.id}`);
        }}
      >
        {initiative.key}
      </Text>
      <img src={getSourceIcon(initiative.source)} width={16} height={16} alt={initiative.source} />
    </Label>
  );
};

export const CompletionCell = ({ initiative }: { initiative: Initiative }) => {
  const progressWidth = Math.min(initiative.percent_progress || 0, 100);
  return (
    <Stack gap={1} mb={4}>
      <Text size="small">{initiative.percent_progress}%</Text>
      <div
        style={{
          width: '100%',
          height: '4px',
          backgroundColor: skyLight,
          borderRadius: '2px',
        }}
      >
        <div
          style={{
            width: `${progressWidth}%`,
            height: '100%',
            backgroundColor: primaryBase,
            borderRadius: '2px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </Stack>
  );
};

export const EndDateCell = ({ initiative }: { initiative: Initiative }) => {
  const i18n = useTranslation();
  return (
    <Group gap={4} justify="space-between" wrap="nowrap">
      <Text size="small" style={{ whiteSpace: 'nowrap' }}>
        {formatDate(initiative.end_date, { fallback: i18n.t('strategy.initiatives.no_date') })}
      </Text>
      {initiative.no_end_date && (
        <Tooltip
          variant="white"
          label={i18n.t('initiatives.section_status.no_end_date')}
          position="top-start"
          multiline
          style={{
            maxWidth: 262,
            lineHeight: '20px',
          }}
        >
          <Icon name="motion_photos_auto" size={16} pointer style={{ marginBottom: 2 }} />
        </Tooltip>
      )}
    </Group>
  );
};
